//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Box } from '@mui/material';

import {
  type ApprovalDecision, type ConversationView, isAgentProgress,
  isTurnResult, type TraceEntry, type TurnResult, type UiAction
} from '@/agent/contract';
import { useInProcessTransport } from '@/agent/useInProcessTransport';
import { useAgentActions } from '@/hooks/useAgentActions';
import { useAsyncJobs } from '@/hooks/useAsyncJobs';
import { useChat } from '@/hooks/useChat';
import { useConfig } from '@/hooks/useConfig';
import { useDataset } from '@/hooks/useDataset';
import { useLLM } from '@/hooks/useLLM';
import { usePanels } from '@/hooks/usePanels';
import { router } from '@/routes';
import {
  ContractVersionError, ConversationGoneError, ProfileNotRecordedError
} from '@/services/agentService';
import { type AsyncStatusResponse } from '@/services/apiTypes';
import { useInvalidateDatasets } from '@/services/xgtQueries';
import { assertNever } from '@/utils/AssertNever';
import { getErrorMessage } from '@/utils/Format';
import ChatContent from './ChatContent';
import { CHAT_PANEL_ID } from './chatPanelId';
import { type ChatMessage } from './ChatTypes';

// What a stored conversation reads as when the panel comes back.  A
// TranscriptApproval (the persisted shape) becomes the same ChatApproval the
// UI layer uses everywhere else, the way a fresh PendingApproval does in
// applyResult below.
const toChatMessages = (view: ConversationView): ChatMessage[] =>
  view.messages.map((message): ChatMessage => ({
    role: message.role === 'user' ? 'user' : 'bot',
    content: message.content,
    ...(message.ui_actions ? { uiActions: message.ui_actions } : {}),
    ...(message.approval ? { approval: {
      id: message.approval.id, summary: message.approval.summary,
      decision: message.approval.decision
    } } : {}),
    ...(message.stopped ?
      { stopped: true, trace: message.trace ?? [],
        ...(message.partial ? { partial: message.partial } : {}) } : {}),
    ...(message.snapshot_dataset !== undefined ?
      { dataset: message.snapshot_dataset } : {})
  }));

const errorMessage = (detail: string): ChatMessage =>
  ({ role: 'bot', content: `> **Error:** ${detail}`, isError: true });

// The one wording of a stop, the same the runtime stores, so the entry
// reads alike whether it arrived in this session or came back on reopen.
const STOPPED_TEXT = 'Stopped. Your next message continues this turn.';

// The backend's existing cancel shape, which the polling helper returns as
// its resolved value rather than throwing -- a finished turn and a
// cancellation both come back from the same await.
const isCancelled = (value: unknown): value is { error_type: 'cancelled' } =>
  typeof value === 'object' && value !== null &&
  'error_type' in value && value.error_type === 'cancelled';

/**
 * What sits inside the chat panel: the turn in flight, the trace it reports,
 * and the model for the next one, which carries the header's dataset.  The
 * transcript and the conversation id live in ChatContext, since the Graphs
 * page also reads the id to fetch a draft the agent named, and the panel's
 * geometry lives in the panel registry.  See docs/design/agent-runtime.md,
 * "Chat Panel Changes" and "Turn Lifecycle".
 */
const ChatPanelBody = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeciding, setIsDeciding] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  // Bumped each time a stop request fails with its turn still running; see
  // ChatContent's comment on the prop this feeds.
  const [stopFailureCount, setStopFailureCount] = useState(0);
  // The turn being polled, for Stop; null between turns. State, not a ref,
  // because ChatContent reads it to know whether there is anything to stop
  // yet -- createConversation and sendTurn are still posting before this is
  // set, and Stop must stay disabled until then rather than accept a press
  // that does nothing.
  const [inFlightAsyncId, setInFlightAsyncId] = useState<string | null>(null);
  // The same id, for handleStop to read once its request settles: the state
  // its closure captured cannot say whether the turn has ended since.
  const inFlightRef = useRef<string | null>(null);
  // runToCompletion's closure holds trace as it stood when the send began,
  // and the cancelled poll itself carries no trace, so the stop entry has to
  // read a ref that onProgress keeps current instead. A step the runtime
  // recorded after the last poll is missing from it until the conversation
  // is reopened, which shows the stored entry.
  const latestTrace = useRef<TraceEntry[]>([]);
  // The reply text the poll has streamed so far, shown as the pending
  // message; the ref is what the cancelled poll reads, for the same reason
  // as latestTrace.
  const [partialText, setPartialText] = useState('');
  const latestPartial = useRef('');
  // Bumped each time a decision is actually written to the transcript, so
  // ChatContent can return focus to the message box -- see its own comment
  // on the prop this feeds.
  const [approvalDecisionCount, setApprovalDecisionCount] = useState(0);
  // Bumped only when an approval arrives from a turn this session ran, so
  // focus moves to the card for a question the user is waiting on and not
  // for one a reopened conversation carries back.
  const [approvalArrivalCount, setApprovalArrivalCount] = useState(0);
  const [trace, setTrace] = useState<TraceEntry[]>([]);
  const { createConversation, getConversation, sendTurn, sendApproval } =
    useAgentActions();
  const { activeLLM } = useLLM();
  const { appConfig } = useConfig();
  const { asyncStatus, cancelAsyncJob } = useAsyncJobs();
  // The contract's spelling of "no dataset" is null; the header's is ''.
  // Converted here, once, so everything below speaks the contract's.
  const {
    activeDataset: headerDataset, choosableDatasets, datasetsLoading
  } = useDataset();
  const activeDataset = headerDataset || null;
  // The datasets the header could switch to, for the rows of suggested
  // prompts below the active dataset's own.
  const otherDatasets = choosableDatasets
    .map((dataset) => dataset.name)
    .filter((name) => name !== headerDataset);
  const {
    chatMessages, setChatMessages, conversationId, setConversationId
  } = useChat();
  const { isPanelOpen, panels } = usePanels();
  const transport = useInProcessTransport();
  const invalidateDatasets = useInvalidateDatasets();

  // Whether this showing may move focus. False for a panel the session
  // restored on page load, which must not drag focus into the message box.
  const takesFocus =
      panels.find(entry => entry.spec.id === CHAT_PANEL_ID)?.takesFocus ??
      false;
  const activeLlmName =
      appConfig?.llm_config?.[activeLLM]?.display_name || activeLLM;
  // No model to send to. Only once the config has loaded: before that the
  // provider has nothing to pick from yet, which is not an admin's doing.
  const noModel = appConfig !== null && activeLLM === '';
  // A pause the user has not answered yet. Posting a new message while one
  // is outstanding would only draw the backend's 409 refusal for it.
  const hasPendingApproval = chatMessages.some(
    (msg) => msg.approval !== undefined && msg.approval.decision === null);

  // Reopen the stored conversation.  Two refusals mean the stored id can
  // never work again -- the conversation predates a contract change, or the
  // server no longer has it at all -- and both are answered the same way:
  // say so, forget it, and start clean.  A conversation that did not record
  // its starting permission profile still reads; only its next turn or
  // approval is refused, which the handlers below answer.
  //
  // `resumed` guards against fetching the conversation this very panel just
  // created: handleSendMessage sets it to the new id in the same tick it
  // calls setConversationId, so by the time this effect sees the new id it
  // finds resumed.current already equal and does nothing. Without that, the
  // effect would fetch an empty conversation right after it was created and
  // overwrite the transcript the user is mid-typing-into with nothing.
  const resumed = useRef<string | null>(null);

  // Bumped by Clear. A send or decision notes it when it starts, and a turn
  // that settles after a Clear writes nothing: its reply, stop or error
  // belongs to the conversation the user threw away, and a stop entry that
  // says the next message continues the turn would be false.
  const clearCount = useRef(0);

  // Forget the stored conversation and say why.  The id is worthless from
  // here on -- a build the server refuses, or a conversation it no longer
  // has, answers every later call the same way -- so keeping it would leave
  // the panel failing identically for ever with nothing saying that Clear is
  // the remedy.
  const startCleanConversation = useCallback((said: string) => {
    resumed.current = null;
    setConversationId(null);
    setChatMessages([{ role: 'bot', content: said }]);
  }, [setChatMessages, setConversationId]);

  useEffect(() => {
    if (conversationId === null || resumed.current === conversationId) return;
    resumed.current = conversationId;
    let cancelled = false;
    (async () => {
      try {
        const view = await getConversation(conversationId);
        if (!cancelled) setChatMessages(toChatMessages(view));
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ContractVersionError) {
          startCleanConversation(
            'This conversation predates an update to the assistant. ' +
            'Starting a new one.');
        } else if (error instanceof ConversationGoneError) {
          startCleanConversation(
            'That conversation is no longer on the server. Starting a new ' +
            'one.');
        } else {
          setChatMessages([errorMessage(getErrorMessage(error))]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, getConversation, setChatMessages,
      startCleanConversation]);

  const onProgress = useCallback((status: AsyncStatusResponse) => {
    if (isAgentProgress(status)) {
      latestTrace.current = status.execution_trace;
      setTrace(status.execution_trace);
      const partial = status.partial_text ?? '';
      latestPartial.current = partial;
      setPartialText(partial);
    }
  }, []);

  // Apply a finished turn to the transcript.  Actions go through the
  // transport, one at a time and in order, so a navigate has landed before
  // the action meant for the destination page is judged against the route;
  // the ones no mounted page took are said in the reply.  `dataset` is the
  // one the turn carried, recorded on the entry that ends it as the runtime
  // records it, so the transcript can note a change; an error entry records
  // none, since a failed turn stores no history.
  const applyResult = useCallback(async (
    result: unknown, dataset: string | null
  ): Promise<void> => {
    if (!isTurnResult(result))
      throw new Error('The turn ended with a body that is not the agent ' +
                      'contract shape');
    const finished: TurnResult = result;
    switch (finished.kind) {
      case 'reply': {
        const unapplied: UiAction[] = [];
        for (const action of finished.ui_actions)
          if (await transport.dispatch(action) === 'not_mounted')
            unapplied.push(action);
        setChatMessages((prev) => [...prev, {
          role: 'bot', content: finished.message,
          uiActions: finished.ui_actions, dataset,
          ...(unapplied.length > 0 ? { unapplied } : {})
        }]);
        break;
      }
      case 'awaiting_approval':
        setChatMessages((prev) => [...prev, {
          role: 'bot', content: finished.message, dataset,
          approval: { id: finished.approval.id,
                      summary: finished.approval.summary, decision: null }
        }]);
        setApprovalArrivalCount((count) => count + 1);
        break;
      case 'error':
        setChatMessages((prev) => [...prev, errorMessage(finished.error)]);
        break;
      default:
        assertNever(finished);
    }
    // The agent may have created or loaded a graph, so the dataset list
    // must not be left stale.
    void invalidateDatasets();
  }, [transport, setChatMessages, invalidateDatasets]);

  const runToCompletion = async (
    asyncId: string, since: number, dataset: string | null
  ): Promise<void> => {
    setTrace([]);
    latestTrace.current = [];
    latestPartial.current = '';
    setPartialText('');
    setInFlightAsyncId(asyncId);
    inFlightRef.current = asyncId;
    try {
      const result = await asyncStatus<unknown>(asyncId, true, 0, onProgress);
      if (clearCount.current !== since) {
        // The turn may still have created or loaded a graph.
        void invalidateDatasets();
        return;
      }
      if (isCancelled(result)) {
        setChatMessages((prev) => [...prev, {
          role: 'bot', content: STOPPED_TEXT, stopped: true, dataset,
          trace: latestTrace.current,
          ...(latestPartial.current ?
            { partial: latestPartial.current } : {})
        }]);
        return;
      }
      await applyResult(result, dataset);
    } finally {
      setInFlightAsyncId(null);
      inFlightRef.current = null;
      setIsStopping(false);
      setPartialText('');
    }
  };

  // Ask the backend to stop the turn. Polling goes on until it confirms:
  // the turn lock is held until the runtime returns, so Send stays blocked
  // and the outcome lands through the same await a reply would.
  const handleStop = async (): Promise<void> => {
    const asyncId = inFlightAsyncId;
    if (asyncId === null) return;
    setIsStopping(true);
    try {
      await cancelAsyncJob(asyncId);
    } catch (error) {
      // A 409 means the turn had already completed, and a 403 or 404 means
      // the poll already consumed the entry cancel would have marked --
      // every one of those says there is no running turn left to stop, and
      // the poll already in flight delivers the result. Any other refusal,
      // such as the 400 for a turn that cannot be cancelled, left the turn
      // running and is reported below.
      const status = axios.isAxiosError(error) ?
        error.response?.status : undefined;
      if (status === 403 || status === 404 || status === 409) return;
      // The turn ended while the request was out, so its outcome is already
      // in the transcript and there is nothing left the stop failed to do.
      if (inFlightRef.current !== asyncId) return;
      setIsStopping(false);
      setStopFailureCount((count) => count + 1);
      setChatMessages((prev) => [...prev, errorMessage(
        `Could not stop the turn: ${getErrorMessage(error)}`)]);
    }
  };

  const handleSendMessage = async (message: string): Promise<void> => {
    const since = clearCount.current;
    setChatMessages((prev) => [...prev, { role: 'user', content: message }]);
    setIsLoading(true);
    try {
      let id = conversationId;
      if (id === null) {
        id = await createConversation();
        // Cleared while it was created: the message went with the
        // transcript, and adopting the id would continue a conversation the
        // user threw away.
        if (clearCount.current !== since) return;
        setConversationId(id);
        resumed.current = id;
      }
      const snapshot = transport.readSnapshot(activeDataset);
      const asyncId = await sendTurn(id, message, snapshot, activeLLM);
      await runToCompletion(asyncId, since, activeDataset);
    } catch (error) {
      if (clearCount.current !== since) return;
      // The conversation the message was addressed to is gone, so the
      // transcript on screen is of something the server cannot continue.
      // The message went nowhere, which the wording has to say: nothing
      // resends it.
      if (error instanceof ConversationGoneError) {
        startCleanConversation(
          'That conversation is no longer on the server. Starting a new ' +
          'one -- send your message again.');
        return;
      }
      // The conversation did not record the permission profile it started
      // under, so the backend will not continue it. The message went nowhere,
      // which the wording has to say: nothing resends it.
      if (error instanceof ProfileNotRecordedError) {
        startCleanConversation(
          'This conversation started before its data permissions were ' +
          'recorded, so it cannot continue. Starting a new one -- send your ' +
          'message again.');
        return;
      }
      // Said in the transcript, where the reader is looking, so a failure
      // is not mistaken for a model still thinking.
      setChatMessages((prev) => [...prev,
                                 errorMessage(getErrorMessage(error))]);
    } finally {
      setIsLoading(false);
      setTrace([]);
    }
  };

  const handleDecide = async (
    approvalId: string, decision: ApprovalDecision
  ): Promise<void> => {
    if (conversationId === null) return;
    const since = clearCount.current;
    // The resumed turn runs on the snapshot stored when it paused, so its
    // entry records that turn's dataset, not the header's now.
    const paused = chatMessages.find((msg) => msg.approval?.id === approvalId);
    const dataset = paused?.dataset ?? null;
    setIsDeciding(true);
    setIsLoading(true);
    try {
      const asyncId = await sendApproval(conversationId, approvalId, decision);
      // Recorded only once the server has accepted it -- writing it
      // beforehand would show a decision that failed to send.
      setChatMessages((prev) => prev.map((msg) =>
        msg.approval?.id === approvalId ?
          { ...msg, approval: { ...msg.approval, decision } } : msg));
      setApprovalDecisionCount((count) => count + 1);
      await runToCompletion(asyncId, since, dataset);
    } catch (error) {
      if (clearCount.current !== since) return;
      // The conversation the decision was for is gone, and with it the
      // approval: left in place, the card would keep the message box closed
      // for a question nothing can answer any more.
      if (error instanceof ConversationGoneError) {
        startCleanConversation(
          'That conversation is no longer on the server, so the decision ' +
          'was not recorded. Starting a new one.');
        return;
      }
      // As above, but the pause the decision answered is gone with it: left
      // in place, the card would keep the message box closed for a question
      // nothing can answer any more.
      if (error instanceof ProfileNotRecordedError) {
        startCleanConversation(
          'This conversation started before its data permissions were ' +
          'recorded, so the decision was not recorded. Starting a new one.');
        return;
      }
      setChatMessages((prev) => [...prev,
                                 errorMessage(getErrorMessage(error))]);
    } finally {
      setIsDeciding(false);
      setIsLoading(false);
      setTrace([]);
    }
  };

  // Escape is the stop key while a turn runs, from anywhere in the body.
  // Swallowed whether or not there is a stop to post: the panel's own
  // Escape parks it, and a press meant to stop the turn, or made while the
  // stop is pending, must not hide the confirmation the user is waiting
  // for. With no turn running it reaches the panel and parks it as before.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || !isLoading) return;
    event.stopPropagation();
    event.preventDefault();
    if (inFlightAsyncId !== null && !isStopping) void handleStop();
  };

  const handleClear = () => {
    clearCount.current += 1;
    setChatMessages([]);
    setConversationId(null);
    resumed.current = null;
  };

  return (
    // ChatContent is four sibling divs that share out the height between
    // them, so it needs a flex column to sit in. The host hands a panel body
    // a plain block box, deliberately: it owns the chrome, not the client's
    // layout model.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
         onKeyDown={handleKeyDown}>
      <ChatContent
        activeLlmName={activeLlmName}
        noModel={noModel}
        chatMessages={chatMessages}
        isVisible={isPanelOpen(CHAT_PANEL_ID)}
        takesFocus={takesFocus}
        onClearChatHistory={handleClear}
        isLoading={isLoading}
        onSendMessage={handleSendMessage}
        activeDataset={activeDataset}
        otherDatasets={otherDatasets}
        datasetsLoading={datasetsLoading}
        trace={trace}
        partialText={partialText}
        hasPendingApproval={hasPendingApproval}
        approvalArrivalCount={approvalArrivalCount}
        approvalDecisionCount={approvalDecisionCount}
        isDeciding={isDeciding}
        onDecide={handleDecide}
        isStopping={isStopping}
        stopFailureCount={stopFailureCount}
        canStop={inFlightAsyncId !== null}
        onStop={() => { void handleStop(); }}
        onNavigate={(path) => { void router.navigate(path); }}
      />
    </Box>
  );
};

export default ChatPanelBody;
